/**
 * Sidebar modules used for staff access control.
 * `pathPrefixes` — any matching route requires this module for `staff` users.
 * Admin / consultant roles are unchanged (role-based only).
 */
export const APP_MODULES = [
  {
    key: 'dashboard',
    label: 'Dashboard',
    pathPrefixes: ['/dashboard']
  },
  {
    key: 'leads',
    label: 'Lead Management',
    pathPrefixes: ['/leads', '/follow-ups', '/edit-lead']
  },
  {
    key: 'students',
    label: 'Manage Students',
    pathPrefixes: ['/students', '/create-student', '/edit-student', '/student-applications']
  },
  {
    key: 'counselors',
    label: 'Manage Counselors',
    pathPrefixes: ['/counselors', '/create-counselor', '/edit-counselor']
  },
  {
    key: 'applications',
    label: 'Manage Applications',
    pathPrefixes: ['/applications', '/create-application', '/edit-application']
  },
  {
    key: 'visas',
    label: 'Visa Management',
    pathPrefixes: ['/visa', '/visa-enquiries', '/create-visa', '/edit-visa', '/edit-visa-enquiry']
  },
  {
    key: 'finance',
    label: 'Finance',
    pathPrefixes: [
      '/finance',
      '/expenses',
      '/create-invoice',
      '/edit-invoice',
      '/invoice-pdf',
      '/payment-receipt'
    ]
  },
  {
    key: 'visitor_logs',
    label: 'Visitor Logs',
    pathPrefixes: ['/visitor-logs', '/add-log', '/edit-log']
  },
  {
    key: 'employees',
    label: 'HR — Employees',
    pathPrefixes: ['/employees']
  },
  {
    key: 'master_data',
    label: 'Master Data',
    pathPrefixes: ['/countries', '/universities', '/intakes']
  },
  {
    key: 'staff_users',
    label: 'Staff Users',
    pathPrefixes: ['/staff-users']
  }
];

export const emptyModulePermissions = () =>
  APP_MODULES.reduce((acc, m) => {
    acc[m.key] = m.key === 'dashboard';
    return acc;
  }, {});

export const parseAllowedModules = (value) => {
  if (!value) return ['dashboard'];
  if (Array.isArray(value)) {
    const list = value.map(String).filter(Boolean);
    return list.includes('dashboard') ? list : ['dashboard', ...list];
  }
  try {
    const parsed = typeof value === 'string' ? JSON.parse(value) : value;
    if (Array.isArray(parsed)) {
      const list = parsed.map(String).filter(Boolean);
      return list.includes('dashboard') ? list : ['dashboard', ...list];
    }
    if (parsed && typeof parsed === 'object') {
      const list = Object.keys(parsed).filter(k => parsed[k]);
      return list.includes('dashboard') ? list : ['dashboard', ...list];
    }
  } catch {
    /* ignore */
  }
  return ['dashboard'];
};

export const serializeAllowedModules = (modules) => {
  let list = [];
  if (Array.isArray(modules)) list = modules.filter(Boolean).map(String);
  else if (modules && typeof modules === 'object') {
    list = Object.keys(modules).filter(k => modules[k]);
  }
  if (!list.includes('dashboard')) list = ['dashboard', ...list];
  return JSON.stringify(list);
};

export const modulesObjectFromList = (list) => {
  const set = new Set(parseAllowedModules(list));
  set.add('dashboard');
  return APP_MODULES.reduce((acc, m) => {
    acc[m.key] = set.has(m.key);
    return acc;
  }, {});
};

export const resolveModuleForPath = (pathname) => {
  if (!pathname) return null;
  const path = pathname.split('?')[0];
  for (const mod of APP_MODULES) {
    for (const prefix of mod.pathPrefixes) {
      if (path === prefix || path.startsWith(`${prefix}/`)) {
        return mod.key;
      }
    }
  }
  return null;
};

export const userHasModule = (user, moduleKey) => {
  if (!user || !moduleKey) return false;
  if (user.role === 'admin' || user.role === 'consultant') return true;
  const allowed = parseAllowedModules(user.allowed_modules);
  return allowed.includes(moduleKey);
};
